import {array, boolean, DecoderError} from '@mojotech/json-type-validation'
import {Result} from '@mojotech/json-type-validation/dist/types/result'
import {Cmd, Loop, loop} from 'redux-loop'
import {actionCreators, displayToasts, getAutomaticRedButtonStatusSucceeded, loginFailed, logoutFailed, notLoggedIn, RootAction, SelectClient, subToClientDataHubFailed, subToClientDataHubSuccessful, userLoggedInSuccessful} from '../actions'
import {greenButtonFailed, orderNoticeAdminResponseReceived, redButtonFailed} from '../actions/redbuttons'
import {createGuid} from '../guids'
import {subToClientDataHub} from '../index'
import {appPermissions} from '../roles-permissions'
import {
  AlertMessage,
  ClientOrderNoticeAdminResult,
  FailedToLogIn,
  HubChannel,
  LimitType,
  LoggedIn,
  AlertLevelsDict,
  Model,
  MutedClientMetricsDict,
  OrderNoticeAdminResponse,
  RawDashboardResponse,
  RJEConnectionStatus,
  User,
} from '../types/types'
import {addToast, assertNever, bySequenceNumberDescending, defaultMetricMutes, delay, highestLevelFromAll, parseDashboardData, runLengthEncode, toDeltas, toGuidLookupTable, updateMetricLevels, updateRecord, updateSortBy} from '../utils'
import {alertMessageDecoder, decodeRJEConnectionStatus, orderNoticeAdminResponseDecoder, rawDashboardResponseDecoder, userDecoder} from "../web/decoders";
import {ClientCode} from "../types/clients";
import {getActiveClients, updateTCSLimits} from "../web/clients";

const initialTime = new Date()

const hubConnectionRetryDelay: number = 5000

export const initialState: Model = {
  alertLevel: 'Baseline',
  alertViewSettings: {
    filterClient: undefined,
    filterDirection: 'all',
    filterMetric: 'all',
    filterSeverityOrLevel: ['all'],
    openColumnMenu: undefined,
    showOnlyActiveAlerts: true,
    sortBy: [['sequenceNumber', 'descending']],
  },
  alerts: [],
  clientSettings: [],
  automaticRedButtonEnabled: 'Unknown',
  clientDetailsPanelOpen: false,
  clientSearch: undefined,
  clients: [],
  currentPage: 'Main Chart',
  dataLastUpdated: undefined,
  greenButtonConfirmationCode: '',
  greenButtonState: 'Raised',
  hubs: {
    clientData: { state: 'NOT_CONNECTED' },
  },
  initialised: true,
  isAddingClient: false,
  languageName: 'TypeScript',
  lastRJEMessageTimestamp: undefined,
  localTime: initialTime,
  metricLevels: {},
  mutedClientMetrics: {},
  orderNoticeAdminResult: undefined,
  redButtonConfirmationCode: '',
  redButtonState: 'Raised',
  selectedClientCode: undefined,
  toasts: [],
  totalMessagesCount: 0,
  dailyTotalNewOrdersValue: 0,
  dailyExecutedOrdersValue: 0,
  user: { type: 'NotLoggedIn' },
}

export function rootReducer(
  state: Model = initialState,
  action: RootAction,
): Loop<Model> | Model {
  switch (action.type) {
    case "SET_CLIENT_SETTINGS":
      return loop<Model>(
        {
          ...state,
          clientSettings: action.clientSettings
        },
        Cmd.none,
      )
    case 'CLOCK_TICK':
      return loop<Model>(
        {
          ...state,
          localTime: action.newLocalTime,
        },
        Cmd.none,
      )

    case 'INIT':
      return loop<Model>(
        state,
        Cmd.run(fetchLoginStatus, { failActionCreator: notLoggedIn, successActionCreator: userLoggedInSuccessful })
      )

    case 'GET_AUTOMATIC_RED_BUTTON_STATUS_SUCCEEDED':
      return loop<Model>(
        {
          ...state,
          automaticRedButtonEnabled: action.enabled ? 'Enabled' : 'Disabled',
        },
        Cmd.none,
      )

    case 'SUBMIT_LOGIN_FORM':
      return loop<Model>(
        {
          ...state,
          user: { type: 'LoggingIn' },
        },
        Cmd.run(
          () => postLogin(action.username, action.password),
          {
            failActionCreator: loginFailed,
            successActionCreator: userLoggedInSuccessful
          },
        ),
      )

    case 'SUBSCRIBE_TO_CLIENT_DATA_HUB_FAILED':
      return loop<Model>(
        {
          ...state,
          hubs: {
            ...state.hubs,
            clientData: {
              state: 'CONNECTING',
            },
          },
        },
        Cmd.run(
          () => delay(hubConnectionRetryDelay).then(subToClientDataHub),
          {
            failActionCreator: subToClientDataHubFailed,
            successActionCreator: subToClientDataHubSuccessful
          }
        ),
      )

    case 'SUBSCRIBE_TO_CLIENT_DATA_HUB_SUCCEEDED':
      if (action.hubConnection === 'SKIPPING') {
        return loop<Model>(state, Cmd.none)
      }

      return loop<Model>(
        {
          ...state,
          // We'll receive all alerts from the server on reconnect, so throw
          // the stale copies away.
          alertLevel: 'Baseline',
          alerts: [],
          hubs: {
            ...state.hubs,
            clientData: {
              connection: action.hubConnection,
              rjeConnectionStatus:
                state.hubs.clientData.state === 'CONNECTED'
                  ? state.hubs.clientData.rjeConnectionStatus
                  : 'Unknown',
              state: 'CONNECTED',
            },
          },
          // We'll also recompute the current metric levels, rather than keep
          // stale data.
          metricLevels: {},
        },
        Cmd.none,
      )

    case 'MESSAGE_FROM_CLIENT_DATA_HUB':
      return handleClientDataHubMessage(action.channel, action.data, state)

    case 'SELECT_CLIENT':
      return handleSelectClient(state, action)

    // TODO: Sort out bug in INIT re fetchUserLoginStatus
    case 'USER_LOGGED_IN':
      switch (action.user.type) {
        case 'FailedToLogIn':
        case 'FailedToLogout':
        case 'LoggingIn':
        case 'NotLoggedIn':
          return loop<Model>(
            {
              ...state,
              user: action.user,
            },
            Cmd.none,
          )
        case 'LoggedIn':
          const destinationPage = appPermissions(
            'canViewMainChart',
            action.user,
          )
            ? 'Main Chart'
            : 'TV Mode'
          return loop<Model>(
            {
              ...state,
              currentPage: destinationPage,
              user: action.user,
            },
            Cmd.list([
              Cmd.run(
                getActiveClients,
                {
                  successActionCreator: actionCreators.setClientSettings
                }
              ),
              Cmd.run( 
                () => subToClientDataHub(),
                {
                  failActionCreator: subToClientDataHubFailed,
                  successActionCreator: subToClientDataHubSuccessful
                }
              ),
              Cmd.run(
                getAutomaticRedButtonStatus,
                {
                  failActionCreator: () =>
                    displayToasts([
                      {
                        content:
                          'Could not retrieve automatic red button armed status',
                        header: 'Error Retrieving Automatic Red Button Status',
                        id: createGuid(),
                      },
                    ]),
                  successActionCreator: getAutomaticRedButtonStatusSucceeded
                }
              ),
            ]),
          )
        default:
          return assertNever(action.user)
      }

    case 'NOT_LOGGED_IN':
      return loop<Model>(
        {
          ...state,
          user: {
            type: 'NotLoggedIn',
          },
        },
        Cmd.none,
      )

    case 'LOGIN_FAILED':
      return loop<Model>(
        {
          ...state,
          user: {
            type: 'FailedToLogIn',
          },
        },
        Cmd.none,
      )

    case 'LOGGING_IN':
      return loop<Model>(
        {
          ...state,
          user: {
            type: 'LoggingIn',
          },
        },
        Cmd.none,
      )

    case 'LOGOUT':
      return loop<Model>(
        state,
        Cmd.run(postLogout, { failActionCreator: logoutFailed, successActionCreator: notLoggedIn }),
      )

    case 'LOGOUT_FAILED':
      return loop<Model>(
        {
          ...state,
          user: markUserFailedToLogOut(state.user),
        },
        Cmd.none,
      )

    case 'MANUALLY_UPDATE_ALERT_LEVEL':
      return loop<Model>(
        {
          ...state,
          alertLevel: action.newLevel,
        },
        Cmd.none,
      )

    case 'TOGGLE_ALERTS_COLUMN_MENU': {
      const openColumnMenu =
        state.alertViewSettings.openColumnMenu === action.column
          ? undefined
          : action.column

      return loop<Model>(
        {
          ...state,
          alertViewSettings: {
            ...state.alertViewSettings,
            openColumnMenu,
          },
        },
        Cmd.none,
      )
    }

    case 'SET_ALERT_COLUMN_SORT': {
      return loop<Model>(
        {
          ...state,
          alertViewSettings: {
            ...state.alertViewSettings,
            sortBy: updateSortBy(
              state.alertViewSettings.sortBy,
              action.field,
              action.sortType,
            ),
          },
        },
        Cmd.none,
      )
    }

    case 'SET_ALERTS_SEVERITY_FILTER': {
      return loop<Model>(
        {
          ...state,
          alertViewSettings: {
            ...state.alertViewSettings,
            filterSeverityOrLevel: action.filter,
          },
        },
        Cmd.none,
      )
    }

    case 'SET_ALERTS_CLIENT_FILTER': {
      return loop<Model>(
        {
          ...state,
          alertViewSettings: {
            ...state.alertViewSettings,
            filterClient: action.filter,
          },
        },
        Cmd.none,
      )
    }

    case 'SET_ALERTS_DIRECTION_FILTER': {
      return loop<Model>(
        {
          ...state,
          alertViewSettings: {
            ...state.alertViewSettings,
            filterDirection: action.filter,
          },
        },
        Cmd.none,
      )
    }

    case 'SET_ALERTS_METRIC_FILTER': {
      return loop<Model>(
        {
          ...state,
          alertViewSettings: {
            ...state.alertViewSettings,
            filterMetric: action.filter,
          },
        },
        Cmd.none,
      )
    }

    case 'CLEAR_ALERTS_FILTERS': {
      return loop<Model>(
        {
          ...state,
          alertViewSettings: {
            ...state.alertViewSettings,
            filterClient: undefined,
            filterDirection: 'all',
            filterMetric: 'all',
            filterSeverityOrLevel: ['all'],
          },
          selectedClientCode: undefined,
        },
        Cmd.none,
      )
    }

    case 'TOGGLE_SHOW_ONLY_ACTIVE_ALERTS': {
      const showOnlyActiveAlerts: boolean =
        action.newValue === 'toggle'
          ? !state.alertViewSettings.showOnlyActiveAlerts
          : action.newValue

      return loop<Model>(
        {
          ...state,
          alertViewSettings: {
            ...state.alertViewSettings,
            showOnlyActiveAlerts,
          },
        },
        Cmd.none,
      )
    }

    case 'UPDATE_RED_BUTTON_STATE': {
      return loop<Model>(
        {
          ...state,
          redButtonState: action.newRedButtonState,
        },
        Cmd.none,
      )
    }

    case 'RED_BUTTON_VIRTUAL_SERVER': {
      return loop<Model>(
        {
          ...state,
          redButtonState: 'Disabled',
        },
        Cmd.run(
          () => postRedButton(action.virtualServerId),
          {
            failActionCreator: () => redButtonFailed(action.virtualServerId),
            successActionCreator: orderNoticeAdminResponseReceived
          }
        )
      )
    }

    case 'RED_BUTTON_FAILED': {
      const orderNoticeAdminResult: ClientOrderNoticeAdminResult = {
        virtualServerId: action.virtualServerId,
        resultMessage:
          'Red button attempt failed due to a server-side error. Please try again.',
        resultType: 'RED',
        success: false,
      }

      return loop<Model>(
        {
          ...state,
          orderNoticeAdminResult,
          redButtonConfirmationCode: '',
          redButtonState: 'Raised',
        },
        Cmd.none,
      )
    }

    case 'ORDER_NOTICE_ADMIN_RESPONSE_RECEIVED': {
      const orderNoticeAdminResult: ClientOrderNoticeAdminResult = {
        virtualServerId: action.virtualServerId,
        resultMessage: action.message,
        resultType: action.button,
        success: action.success,
      }
      switch (action.button) {
        case 'RED':
          return loop<Model>(
            {
              ...state,
              orderNoticeAdminResult,
              redButtonConfirmationCode: '',
              redButtonState: 'Raised',
            },
            Cmd.none,
          )
        case 'GREEN':
          return loop<Model>(
            {
              ...state,
              greenButtonConfirmationCode: '',
              greenButtonState: 'Raised',
              orderNoticeAdminResult,
            },
            Cmd.none,
          )
        default:
          return assertNever(action.button)
      }
    }

    case 'GREEN_BUTTON_VIRTUAL_SERVER': {
      return loop<Model>(
        {
          ...state,
          greenButtonState: 'Disabled',
        },
        Cmd.run(
          () => postGreenButton(action.virtualServerId),
          {
            failActionCreator: () => greenButtonFailed(action.virtualServerId),
            successActionCreator: orderNoticeAdminResponseReceived,
          }
        ),
      )
    }

    case 'GREEN_BUTTON_FAILED': {
      const orderNoticeAdminResult: ClientOrderNoticeAdminResult = {
        virtualServerId: action.virtualServerId,
        resultMessage:
          'Green button attempt failed due to a server-side error. Please try again',
        resultType: 'GREEN',
        success: false,
      }

      return loop<Model>(
        {
          ...state,
          greenButtonConfirmationCode: '',
          greenButtonState: 'Raised',
          orderNoticeAdminResult,
        },
        Cmd.none,
      )
    }

    case 'UPDATE_GREEN_BUTTON_STATE': {
      return loop<Model>(
        {
          ...state,
          greenButtonState: action.newGreenButtonState,
        },
        Cmd.none,
      )
    }

    case 'UPDATE_CLIENT_METRIC_MUTED': {
      const mutedClientMetrics = updateMutedMetrics(
        state.mutedClientMetrics,
        action.clientCode,
        action.metric,
        action.newState,
      )
      const alertLevel = highestLevelFromAll(
        state.clientSettings.reduce(toGuidLookupTable, {}),
        mutedClientMetrics,
        state.metricLevels,
      )
      return loop<Model>(
        {
          ...state,
          alertLevel,
          mutedClientMetrics,
        },
        Cmd.none,
      )
    }

    case 'DISPLAY_TOASTS': {
      return loop<Model>(
        {
          ...state,
          toasts: action.toasts.reduce(addToast, state.toasts),
        },
        Cmd.none,
      )
    }

    case 'REMOVE_TOAST':
      return loop<Model>(
        {
          ...state,
          toasts: state.toasts.filter(t => t.id !== action.id),
        },
        Cmd.none,
      )

    case 'UPDATE_ALL_CLIENT_TCS_LIMITS':
      return loop<Model>(
        state,
        Cmd.run(
          updateTCSLimits,
          {
            failActionCreator: () =>
              displayToasts([
                {
                  content: 'An error occurred while updating client TCS limits',
                  header: 'TCS Limit Update Error',
                  id: createGuid(),
                },
              ]),
            successActionCreator: displayToasts
          }
        )
      )

    default:
      return assertNeverForRedux(action, state)
  }
}

/**
 * Redux is a special case, it needs to pass hidden `action.type`s through our
 * reducer. We can't get exhaustive case checking with the regular `assertNever`
 * because it throws on unexpected values, reinforcing the type system at
 * runtime. Redux explodes if its hidden actions don't get through.
 * See also: https://github.com/reduxjs/redux/issues/186
 */
export function assertNeverForRedux(x: never, y: Model): never {
  return y as never
}

function postLogin(
  username: string,
  password: string,
): Promise<LoggedIn | FailedToLogIn> {
  return fetch('/api/account/login', {
    body: JSON.stringify({ password, username }),
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    method: 'POST',
  })
    .then(response => Promise.all([response, response.json()]))
    .then(([response, body]) => {
      if (!response.ok || response.status === 409) {
        const user: User = {
          type: 'FailedToLogIn',
        }
        return user
      } else {
        return userDecoder.runWithException(body)
      }
    })
}

async function postRedButton(
  virtualServerId: string,
): Promise<OrderNoticeAdminResponse> {
  const response = await fetch('/api/redbutton/' + virtualServerId, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    method: 'POST',
  })

  const body = await response.json()

  if (!response.ok) {
    throw new Error('Red Button Failed')
  }

  const adminResponse = orderNoticeAdminResponseDecoder.runWithException(body)

  return {
    ...adminResponse,
    button: 'RED',
    virtualServerId: virtualServerId,
  }
}

async function postGreenButton(
  virtualServerId: string,
): Promise<OrderNoticeAdminResponse> {
  const response = await fetch('/api/greenbutton/' + virtualServerId, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    method: 'POST',
  })

  const body = await response.json()

  if (!response.ok) {
    throw new Error('Green Button Failed')
  }

  const adminResponse = orderNoticeAdminResponseDecoder.runWithException(body)

  return {
    ...adminResponse,
    button: 'GREEN',
    virtualServerId: virtualServerId,
  }
}

function fetchLoginStatus(): Promise<User> {
  return fetch('/api/account/loginstatus')
    .then(response => Promise.all([response, response.json()]))
    .then(([response, body]) => {
      if (response.ok) {
        return userDecoder.runWithException(body)
      } else {
        const user: User = {
          type: 'NotLoggedIn',
        }
        return user
      }
    })
}

async function getAutomaticRedButtonStatus(): Promise<boolean> {
  const response = await fetch(`/api/config/`, {
    method: 'GET',
  })

  if (response.status === 401) {
    throw new Error('Unauthorised access')
  }

  if (!response.ok) {
    throw response.statusText
  }

  const json = await response.json()
  const redButtonArmed = boolean().runWithException(json)

  return redButtonArmed
}

function postLogout(): Promise<Response> {
  return fetch('/api/account/logout', {
    method: 'POST',
  })
}

function markUserFailedToLogOut(user: User): User {
  switch (user.type) {
    case 'NotLoggedIn':
    case 'FailedToLogIn':
    case 'FailedToLogout':
    case 'LoggingIn':
      return user
    case 'LoggedIn':
      return {
        ...user,
        type: 'FailedToLogout',
      }
    default:
      return assertNever(user)
  }
}

export function handleClientDataHubMessage(
  channel: HubChannel,
  data: unknown,
  model: Model,
): Loop<Model> {
  switch (channel) {
    case 'BroadcastMetricSamples': {
      const decoded: Result<
        RawDashboardResponse,
        DecoderError
      > = rawDashboardResponseDecoder.run(data)
      if (decoded.ok) {
        const parsed = parseDashboardData(decoded.result, model.localTime)
        return loop<Model>(
          {
            ...model,
            clients: parsed.clients,
            dataLastUpdated: parsed.lastUpdated,
            lastRJEMessageTimestamp: parsed.lastRJEMessageTimestamp,
            totalMessagesCount: parsed.totalMessagesCount,
            dailyTotalNewOrdersValue: parsed.dailyTotalNewOrdersValue,
            dailyExecutedOrdersValue: parsed.dailyExecutedOrdersValue,
          },
          Cmd.none,
        )
      }
      console.warn('Failed to decode dashboard data: ', { result: decoded })

      return loop<Model>(
        {
          ...model,
          toasts: addToast(model.toasts, {
            content: 'Failed to decode dashboard data',
            header: 'Critical Error',
            id: createGuid(),
          }),
        },
        Cmd.none,
      )
    }

    case 'BroadcastAlert': {
      const decoded: Result<
        AlertMessage,
        DecoderError
      > = alertMessageDecoder.run(data)
      if (!decoded.ok) {
        console.warn('Failed to decode alert data: ', { result: decoded })

        return loop<Model>(
          {
            ...model,
            toasts: addToast(model.toasts, {
              content: 'Failed to decode dashboard data',
              header: 'Critical Error',
              id: createGuid(),
            }),
          },
          Cmd.none,
        )
      }
      const newAlert = decoded.result
      const alerts: ReadonlyArray<AlertMessage> = [newAlert, ...model.alerts]
      const metricLevels = updateMetricLevels(model.metricLevels, newAlert)
      const alertLevel = highestLevelFromAll(
        model.clientSettings.reduce(toGuidLookupTable, {}),
        model.mutedClientMetrics,
        metricLevels,
      )
      const lastObservedSequenceNumber =
        model.alerts.length > 0 ? model.alerts[0].sequenceNumber : -1

      if (newAlert.sequenceNumber !== lastObservedSequenceNumber + 1) {
        const observedSequenceNumbers = model.alerts.map(a => a.sequenceNumber)
        const sequenceDeltasRunLengthEncoded = toDeltas(observedSequenceNumbers)
          .reduce(runLengthEncode, [])
          .map(runLengthToString)
          .join(', ')

        console.warn(
          `Received alert message out of order (got seq#${
          newAlert.sequenceNumber
          }, wanted seq#${lastObservedSequenceNumber + 1})`,
          {
            newAlert,
            observedSequenceNumbers,
            sequenceDeltasRunLengthEncoded,
          },
        )
      }

      return loop<Model>(
        {
          ...model,
          alertLevel,
          alerts,
          metricLevels,
        },
        Cmd.none,
      )
    }

    case 'BroadcastAlertsHistory': {
      /* This is currently assuming...

      - This message is always received before any newer alerts
      - This message is always received after any older / "stale" alerts

      It also shares some behaviour with the `SUBSCRIBE_TO_CLIENT_DATA_HUB_SUCCEEDED`
      message, in the way they both reset parts of the model. Can / should they
      be de-duplicated?
      */
      const decoded: Result<ReadonlyArray<AlertMessage>, DecoderError> = array(
        alertMessageDecoder,
      ).run(data)
      if (!decoded.ok) {
        console.warn('Failed to decode historical alert data: ', {
          result: decoded,
        })
        return loop<Model>(
          {
            ...model,
            toasts: addToast(model.toasts, {
              content: 'Failed to decode dashboard data',
              header: 'Critical Error',
              id: createGuid(),
            }),
          },
          Cmd.none,
        )
      }
      const historicalAlerts: ReadonlyArray<AlertMessage> = [
        ...decoded.result,
      ].sort(bySequenceNumberDescending)
      const metricLevels: AlertLevelsDict = [...historicalAlerts].reduceRight(
        updateMetricLevels,
        {},
      )
      const alertLevel = highestLevelFromAll(
        model.clientSettings.reduce(toGuidLookupTable, {}),
        model.mutedClientMetrics,
        metricLevels,
      )

      return loop<Model>(
        {
          ...model,
          alertLevel,
          alerts: historicalAlerts,
          metricLevels,
        },
        Cmd.none,
      )
    }

    case 'BroadcastRjeConnectionStatus': {
      const decoded: Result<
        RJEConnectionStatus,
        DecoderError
      > = decodeRJEConnectionStatus.run(data)
      if (!decoded.ok) {
        console.warn('Failed to decode new RJE connection status: ', {
          result: decoded,
        })
        return loop<Model>(
          {
            ...model,
            toasts: addToast(model.toasts, {
              content: 'Failed to decode dashboard data',
              header: 'Critical Error',
              id: createGuid(),
            }),
          },
          Cmd.none,
        )
      }

      switch (model.hubs.clientData.state) {
        case 'CONNECTED':
          return loop<Model>(
            {
              ...model,
              hubs: {
                clientData: {
                  ...model.hubs.clientData,
                  rjeConnectionStatus: decoded.result,
                },
              },
            },
            Cmd.none,
          )
        case 'NOT_CONNECTED':
        case 'CONNECTING':
        case 'RETRY_AFTER':
          // Not expecting to receive any RJE status messages in these states.
          return loop(model, Cmd.none)
        default:
          return assertNever(model.hubs.clientData)
      }
    }

    default:
      return assertNever(channel)
  }
}

function handleSelectClient(
  state: Model,
  action: SelectClient,
): Loop<Model> {
  return loop<Model>(
    {
      ...state,
      greenButtonConfirmationCode: '',
      isAddingClient: false,
      orderNoticeAdminResult: undefined,
      redButtonConfirmationCode: '',
      selectedClientCode: action.select,
    },
    Cmd.none,
  )
}

function runLengthToString([count, value]: [number, number]): string {
  return `${count}× '${value}'`
}

function updateMutedMetrics(
  old: MutedClientMetricsDict,
  clientCode: ClientCode,
  metric: LimitType,
  targetState: 'muted' | 'not_muted' | 'toggle',
): MutedClientMetricsDict {
  const key = clientCode
  const oldMetrics = old[key] || defaultMetricMutes
  const oldState = oldMetrics[metric]
  const newState =
    targetState === 'toggle'
      ? oldState === 'muted'
        ? 'not_muted'
        : 'muted'
      : targetState
  const newMetrics = updateRecord(oldMetrics, metric, newState)
  return updateRecord(old, key, newMetrics)
}
